// ── KdsReadyNotifier.js ──────────────────────────────────────────────────
// Fase 1 de la propuesta "Notificación en tiempo real KDS → POS": polling
// dirigido reutilizando getpedidos.php (el mismo endpoint que ya usa
// KDS.js), sin tocar el backend PHP. Componente autocontenido, montado en
// AppWrapper como hermano de <App/> — se mantiene activo tanto en la
// pantalla de login como durante el uso normal del POS, sin interferir con
// ninguno de los dos (mismo criterio ya usado para VirtualKeyboard).
//
// Activación: solo si config.json de la propiedad tiene "msgkdstopos":"Y".
// Alcance: solo dispositivos con Rol "POS" (currentDeviceConfig en
// localStorage) — KDS/KIC/PRNT no necesitan este aviso.
import React, { useEffect, useState, useCallback } from "react";
import { Bell, X } from "lucide-react";
import { IP } from "../configuracion.js";
import "../styles/KdsReadyNotifier.css";

const POLL_INTERVAL_MS = 20000;

// Marca de tiempo (odate_proceso) del último producto "Producido" ya
// notificado. Se guarda en localStorage (no en un ref) a propósito: si solo
// viviera en memoria, cada vez que se recarga la página el componente
// arrancaría de cero y trataría cualquier producto recién marcado como
// "ya existente" (porque getpedidos.php devuelve TODO el histórico, no solo
// lo de hoy) — silenciándolo igual que si fuera viejo. Guardándolo en
// localStorage, el punto de corte sobrevive a recargas del navegador.
const LAST_SEEN_KEY = "kdsReadyNotifier_lastSeenTimestamp";

// Mismo patrón que playBeep/playNewOrderBeep en KDS.js (Web Audio API, sin
// archivo externo) — copiado en chico aquí para no tener que exportar nada
// de KDS.js ni tocarlo.
const playReadyBeep = () => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    gain.gain.setValueAtTime(0.35, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
    osc.onended = () => {
      try {
        ctx.close();
      } catch (e) {}
    };
  } catch (error) {
    // Sin audio disponible — no debe romper nada.
  }
};

// Mismo locale/velocidad que ya usa KDS.js para sus anuncios de voz.
const speakReady = (text) => {
  try {
    if (!window.speechSynthesis) return;
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = "es-MX";
    utter.rate = 0.9;
    utter.volume = 1;
    window.speechSynthesis.speak(utter);
  } catch (error) {
    // Sin voz disponible — no debe romper nada.
  }
};

const KdsReadyNotifier = () => {
  const [enabled, setEnabled] = useState(false);
  const [pending, setPending] = useState([]); // [{ key, mensaje }]

  // Chequeo de activación: Rol del dispositivo + flag msgkdstopos en
  // config.json. Reintenta cada 5s en vez de intentarlo una sola vez al
  // montar — AppWrapper (y este componente con él) se monta ANTES de que
  // exista propiedad/dispositivo en localStorage (esas pantallas son
  // estados internos de la misma sesión, no un remount). Un solo intento
  // fallido dejaba esto apagado para siempre hasta el próximo reload
  // completo — por eso no se veía nada hasta después de loguearse (que es
  // cuando, por otras razones, terminaba habiendo un reload).
  useEffect(() => {
    let cancelled = false;
    let activated = false;

    const tryActivate = () => {
      if (cancelled || activated) return;

      let deviceConfig = null;
      try {
        const savedDevice = localStorage.getItem("currentDeviceConfig");
        deviceConfig = savedDevice ? JSON.parse(savedDevice) : null;
      } catch (error) {
        deviceConfig = null;
      }
      if (!deviceConfig || deviceConfig.Rol !== "POS") return; // sigue esperando

      const property = localStorage.getItem("currentProperty");
      if (!property) return; // sigue esperando

      fetch(`${IP}/tavpos/backend/data/${encodeURIComponent(property)}/config.json`)
        .then((res) => res.json())
        .then((config) => {
          if (cancelled) return;
          if (config?.msgkdstopos === "Y") {
            activated = true;
            setEnabled(true);
          }
          // Si msgkdstopos no es "Y" no se marca activated — reintenta por
          // si la config cambia mientras el dispositivo sigue encendido.
        })
        .catch(() => {
          // Falla de red puntual — reintenta en el siguiente ciclo.
        });
    };

    tryActivate();
    const retryId = setInterval(tryActivate, 5000);

    return () => {
      cancelled = true;
      clearInterval(retryId);
    };
  }, []);

  const poll = useCallback(async () => {
    const property = localStorage.getItem("currentProperty");
    if (!property) return;

    try {
      const res = await fetch(
        `${IP}/tavpos/backend/getpedidos.php?property=${encodeURIComponent(property)}`
      );
      if (!res.ok) return;
      const data = await res.json();
      if (!data.success || !Array.isArray(data.data)) return;

      // "" (string vacío) en la primera corrida de este dispositivo — como
      // getpedidos.php trae también pedidos de días anteriores, un
      // lastSeen vacío evita que se disparen avisos de todo el histórico
      // la primera vez que esto se activa. A partir de ahí, cada corte
      // queda guardado y ya sobrevive a recargas.
      const lastSeen = localStorage.getItem(LAST_SEEN_KEY) || "";
      let maxTimestamp = lastSeen;
      const nuevos = [];

      data.data.forEach((cheque) => {
        (cheque.pedidos || []).forEach((pedido) => {
          (pedido.productos || []).forEach((prod) => {
            const estado = prod.estado || prod.status || "";
            if (estado !== "Producido") return;

            const ts = prod.odate_proceso || "";
            if (!ts) return;
            // Formato "YYYY-MM-DD HH:mm:ss" — comparar como texto ordena
            // igual que por fecha real, sin depender de zona horaria del
            // navegador vs. la de la propiedad.
            if (ts > maxTimestamp) maxTimestamp = ts;

            if (lastSeen && ts > lastSeen) {
              const mesa = cheque.mesa || "";
              const nombreProducto = prod.nombre || "Producto";
              nuevos.push({
                key: `${prod.Iditem}-${ts}`,
                mensaje: `${nombreProducto} — Mesa ${mesa} listo para entregar (Pedido #${
                  pedido.numero_pedido || pedido.id_pedido
                })`,
                textoVoz: `Pedido listo, mesa ${mesa}`,
              });
            }
          });
        });
      });

      if (maxTimestamp !== lastSeen) {
        localStorage.setItem(LAST_SEEN_KEY, maxTimestamp);
      }
      if (nuevos.length > 0) {
        setPending((prev) => [...prev, ...nuevos]);
        // Un beep por lote nuevo (no uno por producto, para no saturar si
        // llegan varios en el mismo poll) + un anuncio de voz por producto
        // — speechSynthesis ya encola internamente, no hace falta manejarlo.
        playReadyBeep();
        nuevos.forEach((item) => speakReady(item.textoVoz));
      }
    } catch (error) {
      // Silencioso: un poll fallido no debe interrumpir el uso del POS.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    poll();
    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(intervalId);
  }, [enabled, poll]);

  if (!enabled || pending.length === 0) return null;

  const actual = pending[0];

  return (
    <div className="kds-ready-strip" role="status">
      <Bell size={16} className="kds-ready-strip-icon" />
      <span className="kds-ready-strip-msg">{actual.mensaje}</span>
      {pending.length > 1 && (
        <span className="kds-ready-strip-count">+{pending.length - 1} más</span>
      )}
      <button
        type="button"
        className="kds-ready-strip-dismiss"
        onClick={() => setPending((prev) => prev.slice(1))}
        aria-label="Cerrar aviso"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default KdsReadyNotifier;
